<template>
  <span :class="cn(['', `--${isActive ? 'active' : 'inactive'}`])">
    <button @click="onClick" :class="cn('title')">
      {{ link.title }}
    </button>
    <TransitionHeight>
      <ShopifyLinks v-if="isActive" :class="cn('links')" :links="link.links" />
    </TransitionHeight>
  </span>
</template>

<script lang="ts" setup>
import { computed, onMounted } from 'vue'
import { bem } from '../../../utils/classnames'
import { LiquidLink } from '../../../types/liquid'
import TransitionHeight from '../../transition/TransitionHeight.vue'
import ShopifyLinks from './ShopifyLinks.vue'
import { useDropdownInject } from '../../../context/useDropdownContext'
import { useIsDesktop } from '../../../composables/useIsDesktop'

const cn = bem('shopify-link-dropdown')
const { current, set, isTopLevelOpen, openTopLevelOnly } = useDropdownInject()
const { isDesktop } = useIsDesktop('(min-width: 1024px)')

const props = withDefaults(
  defineProps<{
    link: LiquidLink
    depth: number
    index?: number
  }>(),
  { depth: 0, index: 0 }
)

const forceShopOpenPaths = ['/collections/all']

const isShopDropdown = computed(() => {
  const title = props.link.title.toLowerCase()
  return title === 'shop' || title === 'jewelry'
})

const shouldForceShopOpen = computed(() => {
  return isShopDropdown.value && forceShopOpenPaths.includes(window.location.pathname)
})

onMounted(() => {
  const currentPath = window.location.pathname
  const forceShopPaths = ['/blogs/lookbook/jh-x-jb-blunk']

  const shouldSuppressActiveBranch =
    forceShopPaths.includes(currentPath) && props.link.title.toLowerCase() === 'info'

  const isActiveBranch = !!(props.link.active || props.link.child_active)

  if (shouldForceShopOpen.value) {
    if (props.depth === 0 && isDesktop.value) {
      openTopLevelOnly([props.index!])
    } else {
      set(props.link.title)
    }
    return
  }

  if (!isActiveBranch || shouldSuppressActiveBranch) return

  const isTopLevel = props.depth === 0
  if (isTopLevel && isDesktop.value) {
    openTopLevelOnly([props.index!])
  } else {
    set(props.link.title)
  }
})

const isActive = computed(() => {
  if (shouldForceShopOpen.value) return true

  if (props.depth === 0 && isDesktop.value) {
    return isTopLevelOpen(props.index!)
  }

  return current?.value === props.link.title
})

const onClick = () => {
  if (props.depth === 0 && isDesktop.value) {
    if (!isTopLevelOpen(props.index!)) openTopLevelOnly([props.index!])
  } else {
    set(current?.value !== props.link.title ? props.link.title : '')
  }
}
</script>

<style lang="scss"></style>
